import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import MeridianLockup from "../components/MeridianLockup";
import Skeleton from "../components/states/Skeleton";
import { useAuth } from "../lib/auth";
import { capturedTokenHashParams, clearCapturedAuthCallback } from "../lib/authError.mjs";
import { getSupabase, isSupabaseConfigured } from "../lib/supabase";

/**
 * MER-519 / MER-523: set / update password screen. The durable fix for invited
 * users (and forgot-password recovery) who never chose a password and were stuck
 * on one-time, expiring magic links (Seth / Best Disposal).
 *
 * How a user lands here:
 *   - MER-523 prefetch-safe link (recovery + invite): the Supabase email
 *     template links here with `?token_hash=...&type=recovery|invite`. NOTHING
 *     consumes that token on load -- a mail-scanner GET just renders this page.
 *     The token is spent only when the user clicks "Set password": handleSubmit
 *     calls verifyOtp({ token_hash, type }) FIRST (establishing the session),
 *     then updateUser({ password }). That deliberate click is the whole point.
 *   - legacy hash link (backward-compat, pre-template-change): the older
 *     `#access_token&type=recovery|invite` link is auto-exchanged by
 *     detectSessionInUrl into a session before this renders (recovery lands here
 *     directly; invite routes via RootResolver). A present session then skips
 *     verifyOtp and goes straight to updateUser.
 *
 * Show the form when there is EITHER a live session (legacy) OR a pending
 * token_hash to verify (MER-523). No session and no token_hash => the link was
 * missing / already-consumed / expired: show a recovery path, not a dead end.
 */
export default function SetPassword() {
  const navigate = useNavigate();
  const { session, isLoading } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MER-523: capture the token_hash params at first render, BEFORE the mount
  // effect below clears the snapshot. A pending verification (no session yet,
  // but a token_hash in hand) is shown the form and verified on submit.
  const [pending] = useState(capturedTokenHashParams);
  // MER-523: set when verifyOtp rejects the token_hash (expired / already used).
  // It flips the view to the "Link expired" dead-end, which carries the real
  // "Request a new link" CTA -- retrying the form would just re-verify the same
  // dead token and fail identically.
  const [linkInvalid, setLinkInvalid] = useState(false);
  const hasPendingVerification = !session && pending !== null;
  const showForm = !linkInvalid && (Boolean(session) || hasPendingVerification);

  // MER-519: the recovery/invite routing snapshot has done its job once we've
  // landed here, so clear it -- otherwise abandoning this screen (navigating to
  // "/" without setting a password) would bounce back here for the rest of the
  // SPA session. This clears the MODULE snapshot only, never window.location.hash,
  // so it does NOT race detectSessionInUrl (which reads the URL hash to establish
  // the session). For the same reason we deliberately do not replaceState the
  // hash here: Supabase reads it asynchronously and cleans it up itself.
  useEffect(() => {
    clearCapturedAuthCallback();
  }, []);

  if (isLoading) {
    return (
      <main style={{ padding: 32, display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <Skeleton label="Verifying your link" height={80} />
      </main>
    );
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      if (!isSupabaseConfigured()) throw new Error("Set password is not available in this build.");
      const supabase = getSupabase();
      // MER-523: spend the one-time token HERE, on this deliberate submit click,
      // not on page load -- so a mail-scanner GET pre-fetch can't consume it. A
      // legacy hash link already has a session (detectSessionInUrl exchanged it),
      // so `pending` is null there and we skip straight to updateUser.
      if (pending && !session) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: pending.tokenHash,
          type: pending.type,
        });
        if (verifyError) {
          // The token is dead: drop to the "Link expired" view (it has the
          // Request-a-new-link CTA) instead of leaving the user on a form whose
          // resubmit re-verifies the same spent token.
          setLinkInvalid(true);
          setSubmitting(false);
          return;
        }
      }
      const { error: supabaseError } = await supabase.auth.updateUser({ password });
      if (supabaseError) throw supabaseError;
      // Snapshot already cleared on mount; "/" now resolves to the dashboard.
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set password");
      setSubmitting(false);
    }
  }

  return (
    <main
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "32px",
        background:
          "radial-gradient(at 30% 20%, rgba(38,168,159,0.06), transparent 50%), var(--bg)",
      }}
    >
      <div style={cardStyle}>
        <div style={{ display: "grid", justifyItems: "center", gap: 16, marginBottom: 4 }}>
          <MeridianLockup size="lg" align="stack" />
          <h1 style={{ fontSize: 18, fontWeight: 600, color: "var(--text)" }}>
            {showForm ? "Choose a password" : "Link expired"}
          </h1>
        </div>

        {!showForm ? (
          <>
            <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
              This sign-in link has expired or was already used. Request a new reset link, or sign
              in if you already have a password.
            </p>
            <Link
              to="/forgot-password"
              style={{ ...primaryButtonStyle, textAlign: "center", textDecoration: "none" }}
            >
              Request a new link
            </Link>
            <div style={{ textAlign: "center" }}>
              <Link to="/login" style={subtleLinkStyle}>
                Back to sign in
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }}>
            <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", margin: 0 }}>
              Set a password for{session?.user?.email ? ` ${session.user.email}` : " your account"}{" "}
              so you can sign in any time.
            </p>
            <label style={{ display: "grid", gap: 6, fontSize: 13 }}>
              <span style={{ color: "var(--text-muted)" }}>New password</span>
              <input
                type="password"
                required
                autoComplete="new-password"
                data-testid="set-password-new"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 13 }}>
              <span style={{ color: "var(--text-muted)" }}>Confirm password</span>
              <input
                type="password"
                required
                autoComplete="new-password"
                data-testid="set-password-confirm"
                value={confirm}
                onChange={(ev) => setConfirm(ev.target.value)}
                style={inputStyle}
              />
            </label>

            {error ? (
              <div
                role="alert"
                style={{
                  fontSize: 13,
                  color: "var(--flag-red)",
                  background: "rgba(192, 57, 43, 0.08)",
                  padding: "10px 12px",
                  borderRadius: 8,
                }}
              >
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              data-testid="set-password-submit"
              style={primaryButtonStyle}
            >
              {submitting ? "Saving..." : "Set password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 16,
  padding: "36px 36px 28px",
  width: "100%",
  maxWidth: 380,
  boxShadow: "var(--shadow-card)",
  display: "grid",
  gap: 14,
};

const inputStyle: React.CSSProperties = {
  font: "inherit",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--border-subtle)",
  background: "var(--bg-elevated)",
  color: "var(--text-primary)",
};

const primaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "12px 14px",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(95deg, var(--brand-deep) 0%, var(--brand-teal) 100%)",
  color: "white",
  cursor: "pointer",
  letterSpacing: 0.3,
};

const subtleLinkStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--brand-deep)",
  textDecoration: "none",
};
